import { useCallback, useEffect, useState } from 'react';
import { withDashboardLayout } from 'layouts/DashboardLayout/DashboardLayout';
import { Link, useHistory, useParams } from 'react-router-dom';
import { useAlert } from 'context/AlertProvider';
import {
  Button,
  Card,
  Dimmer,
  Divider,
  Icon,
  Label,
  Loader,
  Message,
} from 'semantic-ui-react';
import PageContainer from 'components/PageContainer/PageContainer';
import RetryShipmentForm from 'components/RetryShipmentForm/RetryShipmentForm';
import Portal from 'components/Portal/Portal';
import { ORIGINS, STATUSES } from 'constants/constants';
import { getMessageFromAxiosError } from 'modules/message-factory/message-factory';
import { fix, getSingle } from 'services/shipments.service';

export const originsPaths = {
  [`${ORIGINS.SCANPACK.slug}`]: '/retry-scanpack-shipments',
  [`${ORIGINS.SORTER.slug}`]: '/retry-sorter-shipments',
  [`${ORIGINS.LOLINA.slug}`]: '/retry-lolina-shipments',
  [`${ORIGINS.BULKDROP.slug}`]: '/retry-bulkdrop-shipments',
};

function RetryShipmentPage({ origin }) {
  const history = useHistory();
  const { reference } = useParams();
  const [loading, setLoading] = useState(false);
  const [shipment, setShipment] = useState(null);
  const alert = useAlert();

  const path = originsPaths[origin] || '/retry-shipments';

  const checkAndRedirect = useCallback(
    (error) => {
      const errorType = error?.response?.data?.detail?.type;
      if (!errorType) return;
      const redirectableErrorTypes = ['shipment.not_found'];
      if (redirectableErrorTypes.includes(errorType)) {
        history.push('/retry-shipments');
      }
    },
    [history],
  );

  const getShipment = useCallback(
    async (reference) => {
      try {
        setLoading(true);
        const { data: shipment } = await getSingle({ reference });
        setShipment(shipment);
      } catch (error) {
        alert.current.error(getMessageFromAxiosError(error), () =>
          checkAndRedirect(error),
        );
      } finally {
        setLoading(false);
      }
    },
    [alert, checkAndRedirect],
  );

  const updateShipment = useCallback(
    async (payload) => {
      try {
        setLoading(true);
        await fix(reference, payload);
        alert.current.message('Envío actualizado con éxito', () => {
          history.push(path);
        });
      } catch (error) {
        alert.current.error(getMessageFromAxiosError(error));
      } finally {
        setLoading(false);
      }
    },
    [alert, history, reference, path],
  );

  useEffect(() => {
    (async () => await getShipment(reference))();
  }, [getShipment, reference]);

  return (
    <PageContainer
      title={
        <>
          Referencia de envío: {reference}
          {shipment && (
            <Label color={STATUSES[shipment.status]?.color}>
              {STATUSES[shipment.status]?.text}
            </Label>
          )}
        </>
      }
      className="retry-shipment-page"
    >
      <Dimmer active={loading} inverted>
        <Loader>Cargando…</Loader>
      </Dimmer>
      <>
        <Card.Header>
          <Link to={path}>
            <Button
              basic
              circular
              content={
                <>
                  <Icon name="arrow left" />
                  Volver
                </>
              }
            />
          </Link>
          <div>
            <Portal.Out name="card-header" />
          </div>
        </Card.Header>
        <Card.Content>
          {!shipment?.error ? (
            <Message positive>
              <Message.Header>¡Todo correcto!</Message.Header>
              <p>El envío se puede reintentar sin modificar</p>
            </Message>
          ) : (
            <Message negative>
              <Message.Header>Error procesando el envío</Message.Header>
              <p>{shipment.error}</p>
            </Message>
          )}
          <Divider style={{ margin: '1.5rem 0' }} />
          {!!shipment && (
            <RetryShipmentForm
              shipment={shipment}
              onUpdateShipment={updateShipment}
            />
          )}
        </Card.Content>
      </>
    </PageContainer>
  );
}

export default withDashboardLayout(RetryShipmentPage);
